// src/pages/LoginPage.jsx
import { useEffect, useRef, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { AnimatePresence, motion } from "framer-motion";

import { Toast } from "primereact/toast";

import "./css/LoginPage.css";
import illustration from "../images/logo-mykyc-login-blue.png";

function friendlyAuthError(e) {
  const code = String(e?.code || "");
  const msg = String(e?.message || "");

  // Common Firebase auth errors -> nicer messages
  if (code.includes("auth/invalid-email"))
    return "Please enter a valid email address";
  if (code.includes("auth/missing-email")) return "Email is required";
  if (code.includes("auth/missing-password")) return "Password is required";
  if (code.includes("auth/user-not-found"))
    return "No account was found with this email";
  if (code.includes("auth/wrong-password"))
    return "Incorrect password. Please try again";
  if (code.includes("auth/invalid-credential"))
    return "Incorrect email or password";
  if (code.includes("auth/user-disabled"))
    return "This account has been disabled. Please contact an administrator";
  if (code.includes("auth/too-many-requests"))
    return "Too many attempts. Please wait a moment and try again";
  if (code.includes("auth/network-request-failed"))
    return "Network error. Please check your connection and try again";

  // Fallback
  return msg || "Login failed. Please try again";
}

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const toastRef = useRef(null);

  const [redirectTo] = useState(() => {
    const loggedOut = sessionStorage.getItem("mykyc:loggedOut") === "1";
    if (loggedOut) sessionStorage.removeItem("mykyc:loggedOut");
    const from = loc.state?.from?.pathname;
    const deepLinkPrefixes = ["/kyc-review/", "/kyc/"];
    return !loggedOut && from && deepLinkPrefixes.some((p) => from.startsWith(p))
      ? from
      : "/clients";
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const canSubmit = email.trim() && password;

  const emailRef = useRef(null);
  const [animDone, setAnimDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setAnimDone(true);
      emailRef.current?.focus();
    }, 1300); // 220ms page fade + 700ms card animation + 380ms extra delay
    return () => clearTimeout(t);
  }, []);

  // animated shaking effect on error
  // if you don t want shaking effect remove motion.div on <div className="loginCard">
  // const [shake, setShake] = useState(false);

  useEffect(() => {
    sessionStorage.removeItem("mykyc:suppressClientsLogoutLoader");
  }, []);

  // ✅ Show toast + error when redirected here (blocked org / blocked user)
  useEffect(() => {
    const msg = loc.state?.authError;
    const toastId = loc.state?.toastId;

    if (!msg || !toastId) return;

    const key = `login_authError_${toastId}`;
    if (sessionStorage.getItem(key)) return;

    sessionStorage.setItem(key, "1");
    setErr(msg);

    // ✅ clear location.state so it doesn't re-appear on refresh
    nav("/login", { replace: true, state: {} });
  }, [loc.state, nav]);
  // useEffect(() => {
  //   const loginError = loc.state?.loginError;
  //   const toast = loc.state?.toast;
  //   const toastId = loc.state?.toastId;

  //   if (loginError) {
  //     setErr(loginError);
  //   }

  //   // show toast only ONCE (even if page reloads)
  //   if (toast && toastId) {
  //     const key = `toast_consumed_${toastId}`;
  //     if (!sessionStorage.getItem(key)) {
  //       sessionStorage.setItem(key, "1");
  //       toastRef.current?.show(toast);
  //     }
  //   }

  //   // ✅ clean URL state so refresh doesn't show it again
  //   if (loginError || toast) {
  //     nav("/login", { replace: true, state: {} });
  //   }
  //   // eslint-disable-next-line
  // }, []);

  async function onSubmit(e) {
    e.preventDefault();

    try {
      setErr("");
      setLoading(true);

      const cleanEmail = email.trim().toLowerCase();
      const cleanPassword = password;

      if (!cleanEmail) {
        setErr("Email is required");
        return;
      }
      if (!cleanPassword) {
        setErr("Password is required");
        return;
      }

      await signInWithEmailAndPassword(auth, cleanEmail, cleanPassword);

      nav(redirectTo, { replace: true });
    } catch (e) {
      //animated shaking effect
      // setShake(true);
      // setTimeout(() => setShake(false), 350);
      // display error in red box
      setErr(friendlyAuthError(e));
      // setPassword("");
      const code = e?.code || "";
      if (
        code === "auth/wrong-password" ||
        code === "auth/invalid-credential"
      ) {
        setPassword("");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="loginPage">
      <div className="loginCardWrap">
      <div className="loginCard">
        {/* LEFT: form */}
        <div className="loginLeft">
          <h3 className="loginTitle">Sign in</h3>
          <div className="loginSub">Staff access</div>

          {/* {err && <div className="alert alert-danger">{err}</div>} */}
          <AnimatePresence mode="wait">
            {err ? (
              <motion.div
                key="loginError"
                initial={{ opacity: 0, y: -8, height: 0 }}
                animate={{ opacity: 1, y: 0, height: "auto" }}
                exit={{ opacity: 0, y: -8, height: 0 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                style={{ overflow: "hidden" }}
              >
                <div className="alert alert-danger loginErrorAlert">{err}</div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <form
            onSubmit={onSubmit}
            className="loginForm"
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              if (!canSubmit || loading) return;
              e.preventDefault();
              e.currentTarget.requestSubmit();
            }}
          >
            <input
              ref={emailRef}
              className="form-control"
              placeholder="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (err) setErr("");
              }}
              autoComplete="email"
              tabIndex={animDone ? undefined : -1}
            />

            <input
              className="form-control"
              placeholder="Password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (err) setErr("");
              }}
              autoComplete="current-password"
              tabIndex={animDone ? undefined : -1}
            />

            <button
              type="submit"
              className={`btn btn-primary loginBtn${
                !canSubmit ? " isIncomplete" : ""
              }`}
              disabled={!canSubmit || loading}
              tabIndex={animDone ? undefined : -1}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>

        {/* RIGHT: illustration */}
        <div className="loginRight" aria-hidden="true">
          <img
            src={illustration}
            alt=""
            className="loginIllustration"
            draggable="false"
          />
        </div>
      </div>
      </div>

      <Toast ref={toastRef} />
      <div className="loginDisclaimer">
        <span className="loginDisclaimerLabel">Disclaimer:</span>{" "}
        MyKYC provides information and functionality intended to assist compliance processes. Users remain solely responsible for all compliance, onboarding, due diligence, risk assessment and business decisions. Screening results may be incomplete, inaccurate or outdated and should be independently reviewed and verified before being relied upon.
      </div>
      <div className="loginFooter">
        © Naxero Digital Technology - {new Date().getFullYear()}. All rights reserved.
      </div>
    </div>
  );
}
